define('bindingHandlers/dataTable/dataTable',[
    'jquery',
    'lib/bluebird',
    'lib/knockout',
    'lib/lodash4',

    'viewModels/widgets/Pager',

    'util/domUtils',
    'util/bindingHandlerUtils',
    'util/keyCode',
    'util/externalInclude',

    'text!templates/widgets/dataTable/dataTableCustomizeDropdown.html'
], function (
    $,
    Promise,
    ko,
    _,

    Pager,

    domUtils,
    bindingHandlerUtils,
    keyCode,
    include,

    customizeTemplate
) {
    'use strict';

    // ===================================================================================
    // Private Variables
    // ===================================================================================

    var DATA_TABLE_CLASS    = "data-table";
    var TABLE_LAYOUT_CLASS  = "l-auto-table";
    var SCROLL_LAYOUT_CLASS = "l-scroll-table";

    var ROW_CLASS        = "dt-row";
    var ROW_ACTIVE_CLASS = "dt-row-active";
    var ROW_PINNED_CLASS = "dt-row-pinned";
    var LAST_ROW_PINNED_CLASS = "last-pinned";
    var COLUMN_PREFIX    = 'dt-column-';
    var GRID_CELL        = 'dt-cell';
    var VALUE_CELL       = 'value-cell';
    var SELECTABLE_CELL  = 'selectable-cell';
    var PINNABLE_CELL    = 'pinnable-cell';
    var HIDDEN_CLASS     = 'hidden';
    var TABLE_AUTOSIZING = 'dt-auto-sizing';

    var EVENT_NAMESPACE = ".data-table-event";
    var FILTER_SELECTORS = "input, select";

    var HEADER_ROW_TEMPLATE = 'templates/widgets/dataTable/headerRow';
    var TABLE_DOMDATA_KEY = "data-table-binding-data";
    var DATA_OLD_VALUE_KEY = "data-table-oldValue";
    var MANUAL_FILTER_DEBOUNCE_DELAY = 200;
    var AUTO_FILTER_DEBOUNCE_DELAY = 1000;

    // ===================================================================================
    // Private Methods
    // ===================================================================================

    //
    // Utility
    //

    function getLayoutClass($element) {
        if ($element.hasClass(TABLE_LAYOUT_CLASS)) {
            return TABLE_LAYOUT_CLASS;
        }

        return SCROLL_LAYOUT_CLASS;
    }

    function isDisplayingVerticalScrollbar ($element) {
        if (!_.isNull($element) && !_.isUndefined($element)) {
            return $element[0].scrollHeight > $element.height();
        }
        return false;
    }

    function isDisplayingHorizontalScrollbar ($element) {
        if (!_.isNull($element) && !_.isUndefined($element)) {
            return $element[0].scrollWidth > $element.width();
        }
        return false;
    }

    function updateSelectedRows (table) {
        var selectedRowsCollection = table._options.selectedRows;
        var selectedViewModels = [];

        _.each($("." + ROW_ACTIVE_CLASS, table._body), function (element) {
            var rowViewModel = ko.dataFor(element);

            selectedViewModels.push(rowViewModel);
        });
        if(selectedRowsCollection) {
            selectedRowsCollection(selectedViewModels);
        }
    }

    function updatePinnedRows (table) {
        var pinnedRowsCollection = table._options.pinnedRows;
        var pinnedViewModels = pinnedRowsCollection();

        _.each($('.dt-row', table._body), function (element) {
            var rowViewModel = ko.dataFor(element);
            var wasPinned = _.find(pinnedViewModels, rowViewModel);
            var isPinned = $(element).hasClass(ROW_PINNED_CLASS);

            if (isPinned && !wasPinned) {
                pinnedViewModels.push(rowViewModel);
            } else if (!isPinned && wasPinned) {
                pinnedViewModels = _.filter(pinnedViewModels, function (row) {
                    return row.id !== rowViewModel.id;
                });
            }
        });

        pinnedRowsCollection(pinnedViewModels);
    }

    function areFiltersModified (table) {
        var shouldDisplayFilterSection = false;
        _.forEach($(FILTER_SELECTORS, table._filterSection), function(inputNode) {
            var $input = $(inputNode);
            if ($input.data(DATA_OLD_VALUE_KEY) !== $input.val()) {
                shouldDisplayFilterSection = true;
                return false;
            }
        });

        return shouldDisplayFilterSection;
    }

    function onFilterInputModification (table) {
        if (areFiltersModified(table)) {
            if (table._autoApplyFilters) {
                computeDataOptions(table, true);
            }
            else {
                table._applyFilterSection.removeClass(HIDDEN_CLASS);
            }

        } else {
            table._applyFilterSection.addClass(HIDDEN_CLASS);
        }
    }

    function saveFilterValues (table) {
        _.forEach($(FILTER_SELECTORS, table._filterSection), function(inputNode) {
            var $input = $(inputNode);
            $input.data(DATA_OLD_VALUE_KEY, $input.val());
        });
    }

    //
    // Cached Template Getters
    //

    var getNoDataTemplate = function (options) {
        var $NO_DATA_TEMPLATE = $('<div class="dt-row dt-row-empty dt-no-data">')
                                    .append('<div class="dt-cell" data-bind="lString: \'' + options.noDataTextKey + '\'">');

        ko.applyBindings({}, $NO_DATA_TEMPLATE[0]);

        return $NO_DATA_TEMPLATE;
    };

    var getLoadingDataTemplate = _.once(function () {
        var $LOADING_DATA_TEMPLATE = $('<div class="dt-row dt-row-empty dt-is-loading">')
            .append('<div class="dt-cell" data-bind="lString: \'dataGrid.loading\'">');

        ko.applyBindings({}, $LOADING_DATA_TEMPLATE[0]);
        return $LOADING_DATA_TEMPLATE;
    });

    var getPinColumnTemplate = _.once(function() {
        var $pinNode = $('<button type="button" class="btn btn-flat">')
                            .append('<i class="pc pc-icon-thumbtack-off pin-unpinned">')
                            .append('<span class="sr-only pin-pinned" data-bind="lString: \'dataGrid.pinItem\'"></span>')

                            .append('<i class="pc pc-icon-thumbtack-on pin-pinned">')
                            .append('<span class="sr-only pin-unpinned" data-bind="lString: \'dataGrid.unpinItem\'"></span>');

        var $pinCol = $("<div>")
                        .addClass(GRID_CELL)
                        .addClass(VALUE_CELL)
                        .addClass(PINNABLE_CELL)
                        .append($pinNode);

        return $pinCol;
    });

    var getCheckboxColumnTemplate = _.once(function () {
        // Since we cannot modify the view model that backs each row in the table and
        // use a knockout template, we instead resort to rendering all of the potential
        // states for the custom checkbox. We then utilize CSS and display:none
        // (See: Data Table in Style Guide) to determine the display state of a checkbox.
        //
        // When the row is selected and gets the "dt-row-active" class toggled on it, the
        // CSS rules applied to the row will determine which state of the checkbox gets
        // displayed.
        var $checkboxNode = $('<button type="button" class="btn btn-flat">')
                                .append('<i class="fa fa-fw fa-square-o checkbox-unchecked">')
                                .append('<span class="sr-only checkbox-checked" data-bind="lString: \'dataGrid.selectItem\'"></span>')

                                .append('<i class="fa fa-fw fa-check-square-o checkbox-checked">')
                                .append('<span class="sr-only checkbox-unchecked" data-bind="lString: \'dataGrid.deselectItem\'"></span>');

        var $checkBoxCol = $("<div>")
                            .addClass(GRID_CELL)
                            .addClass(VALUE_CELL)
                            .addClass(SELECTABLE_CELL)
                            .append($checkboxNode);

        return $checkBoxCol;
    });

    var getEmptyColumnTemplate = _.once(function () {
        var $emptyDiv = $('<div>')
                        .addClass(GRID_CELL)
                        .addClass(VALUE_CELL);
        return $emptyDiv;
    });

    var getEmptyFilterCellTemplate = _.once(function () {
        return $('<div class="dt-cell filter-cell"/>');
    });

    //
    // Table Template Creation
    //

    function buildColumnTemplates ($columnElements, cellClasses) {
        return _.reduce($columnElements, function (result, element) {
            var $element = $(element).addClass(GRID_CELL).addClass(cellClasses).remove();
            result[$element.attr('data-column')] = $element;
            return result;
        }, {});
    }

    function buildFilterColumnTemplates (table) {
        return buildColumnTemplates($(table._element).find("[data-filter-row] > [data-column]"), "filter-cell");
    }

    function buildContentColumnTemplates (table) {
        return buildColumnTemplates($(table._element).find("[data-row] > [data-column]"), VALUE_CELL);
    }

    function buildSummaryColumnTemplates (table) {
        return buildColumnTemplates($(table._element).find("[data-summary-row] > [data-column]"), VALUE_CELL);
    }



    function buildRowTemplate(table, columnTemplateMap, $rowTemplate, $emptyCellTemplate) {
        var columns = ko.unwrap(table._visibleColumns);

        _.each(columns, function (column) {
            var element = columnTemplateMap[column.id];
            if (element) {
                $rowTemplate.append(element);

                $(element).addClass(COLUMN_PREFIX + column.id);
            } else if ($emptyCellTemplate) {
                // If there is no template for the column's cell, use the provided empty
                // cell template instead.
                $rowTemplate.append($emptyCellTemplate.clone().addClass(COLUMN_PREFIX + column.id));
            }
        });

        return $rowTemplate;
    }

    function buildFilterRowTemplate (table) {
        var $filterRowTemplate = $(table._element)
                                    .children("[data-filter-row]")
                                    .addClass(ROW_CLASS)
                                    .remove();

        if (table._hasFilterRow && $filterRowTemplate.length === 0) {
            // When we re-render the displayed columns, we need to instead use the current
            // template's root node since the original one does not exist anymore.
            $filterRowTemplate = $(table._filterRowTemplate()).clone().empty();
        }

        if (table._isMultiRowSelection) {
            $filterRowTemplate.append(getEmptyFilterCellTemplate().clone().addClass(SELECTABLE_CELL));
        }

        $filterRowTemplate = buildRowTemplate(
            table,
            table._filterColumnTemplates,
            $filterRowTemplate,
            getEmptyFilterCellTemplate()
        );

        if (table._allowRowPinning) {
            var $emptyPinCell = getEmptyFilterCellTemplate().clone().addClass(PINNABLE_CELL);
            $filterRowTemplate.append($emptyPinCell);
        }

        return $filterRowTemplate[0];
    }

    function buildSummaryRowTemplate (table) {
        var $rowTemplate = $(table._element)
                                    .children("[data-summary-row]")
                                    .addClass(ROW_CLASS)
                                    .remove();

        if (table._hasSummaryRow() && $rowTemplate.length === 0) {
            $rowTemplate = $(table._summaryRowTemplate()).clone().empty();
        }

        if (table._isMultiRowSelection) {
            $rowTemplate.append(getEmptyColumnTemplate().clone());
        }

        $rowTemplate = buildRowTemplate(table, table._summaryColumnTemplates, $rowTemplate);

        if (table._allowRowPinning) {
            $rowTemplate.append(getEmptyColumnTemplate().clone());
        }

        return $rowTemplate[0];
    }

    function buildContentRowTemplate (table) {
        var $rowTemplate = $(table._element)
                                .children("[data-row]")
                                .addClass(ROW_CLASS)
                                .remove();

        if ($rowTemplate.length === 0) {
            $rowTemplate = $(table._rowTemplate()).clone().empty();
        }

        if (table._isMultiRowSelection) {
            $rowTemplate.addClass("dt-row-selectable");
            $rowTemplate.append(getCheckboxColumnTemplate().clone());
        }

        $rowTemplate = buildRowTemplate(table, table._contentColumnTemplates, $rowTemplate);

        if (table._allowRowPinning) {
            $rowTemplate.addClass("dt-row-pinnable");
            $rowTemplate.append(getPinColumnTemplate().clone());
        }

        return $rowTemplate[0];
    }



    function buildFooterTemplate (table) {
        var $footerTemplate = $(table._element).find("[data-footer]").addClass("table-controls").addClass("footer-controls").remove();

        if ($footerTemplate.length === 0) {
            return undefined;
        }

        var opts = table._options;

        var $pager = $footerTemplate.children("[data-pager]").remove();
        var $settings = $footerTemplate.children("[data-settings]").remove();

        if ($pager.length === 0 && $settings.length === 0) {
            return undefined;
        }

        var $footerContent = $("<div class=\"control-section\">");
        if (_.isUndefined(opts.alternatePager) && $pager.length > 0) {
            var $context = $(table._element);
            // render the pager
            // custom maxPerPage
            if (opts.maxPageSize) {
                var allowedPageSizes = [25, 50, 100];
                var pageSizes = allowedPageSizes.filter(function(num) {
                    return num <= opts.maxPageSize;
                });
                if (pageSizes.length === 0) {
                    pageSizes = [25];
                }
                table._pagerView = new Pager({ allowedPageSizes: pageSizes });
            } else {
                table._pagerView = new Pager();
            }

            // scroll to top of content when switching pages
            if (!_.isUndefined($context) && $context.length > 0) {
                table._scrollSubscribe = table._pagerView.pageNumber.subscribe(function() {
                    $context.find('.scrollable-section').scrollTop(0);
                });
            }

            $footerContent.append("<div class=\"loading load-indicator-sm\" data-bind=\"loadIndicator: isLoadingRows\">");
            $footerContent.append("<div data-bind=\"include: { name: 'templates/widgets/pager', data: pager }\">");

            $footerTemplate.append($footerContent);
        } else if (!_.isUndefined(opts.alternatePager) && $pager.length > 0) {
            table._alternatePager = opts.alternatePager.view;
            $footerContent.append("<div class=\"loading load-indicator-sm\" data-bind=\"loadIndicator: isLoadingRows\">");
            $footerContent.append("<div data-bind=\"include: { name: '" + opts.alternatePager.template + "', data: alternatePager }\">");

            $footerTemplate.append($footerContent);
        }

        if ($settings.length > 0 && table._options.visibleColumnIds) {
            // TODO: The content of the customize buttons don't do anything yet, but at least
            // rendering the buttons are there for now
            var $customizeControl = $("<div>").addClass("pull-controls").append(customizeTemplate);
            $footerTemplate.append($customizeControl);
            table._isDisplayingCustomizeOptions = ko.observable(false);
            table._isReset = ko.observable(false);
        }

        return $footerTemplate[0];
    }

    //
    // Table Content Rendering
    //

    function HeaderRowViewModel (table) {
        var self = this;

        self.columns = table._visibleColumns;
        self.allowRowPinning = table._allowRowPinning;
        self.isMultiRowSelection = table._isMultiRowSelection;
        self.isSortByAscending = table._isSortByAscending;
        self.sortByColumnName = table._sortByColumnName;

        // ===========================================================================
        // Row Selection State
        // ===========================================================================

        self.noRowsSelected = ko.computed(function () {
            if (!_.has(table._options, "selectedRows")) {
                return false;
            }

            return ko.unwrap(table._options.selectedRows).length === 0;
        });

        self.allRowsSelected = ko.computed(function () {
            if (!_.has(table._options, "selectedRows")) {
                return false;
            }

            var rowViewAmount = ko.unwrap(table._options.rows).length;

            return rowViewAmount > 0 && ko.unwrap(table._options.selectedRows).length === rowViewAmount;
        });

        self.partialRowsSelected = ko.computed(function () {
            if (!_.has(table._options, "selectedRows")) {
                return false;
            }

            return !self.noRowsSelected() && !self.allRowsSelected();
        });

        self.onToggleAllRowSelection = function () {
            if (self.noRowsSelected()) {
                $("." + ROW_CLASS, table._body).not('.dt-row-empty').addClass(ROW_ACTIVE_CLASS);
            } else {
                $("." + ROW_ACTIVE_CLASS, table._body).removeClass(ROW_ACTIVE_CLASS);
            }

            updateSelectedRows(table);
        };

        // ===========================================================================
        // Cell Sorting
        // ===========================================================================

        self.onSortableCellClick = function (columnId) {
            if (self.sortByColumnName.peek() === columnId) {
                self.isSortByAscending(!self.isSortByAscending.peek());
            } else {
                self.sortByColumnName(columnId);
                self.isSortByAscending(true);
            }
        };

        //
        // Determine the display state of the caret for a given column. We only
        // display the caret on a column if it is the selected column.
        //

        self.isColumnSortAscending = function (columnId) {
            var self = this;

            return self.isSortByAscending() && self.sortByColumnName() === columnId;
        };

        self.isColumnSortDescending = function (columnId) {
            var self = this;

            return !self.isSortByAscending() && self.sortByColumnName() === columnId;
        };

        //
        // Generate accessibility attributes for sortable columns
        //

        self.ariaLabelForColumnId = function (columnId) {
            var self = this;

            var sortedColumn = _.find(self.columns(), function (column) {
                return column.id === columnId;
            });

            var columnLabel = ko.i18n.renderString(sortedColumn.label);

            if (self.sortByColumnName() !== columnId || !self.isSortByAscending()) {
                return ko.i18n.renderString("dataTable.activateSortAscending", {
                    columnLabel: columnLabel
                });
            }

            return ko.i18n.renderString("dataTable.activateSortDescending", {
                columnLabel: columnLabel
            });
        };

        self.ariaSortForColumnId = function (columnId) {
            var self = this;

            if (self.sortByColumnName() !== columnId) {
                return "none";
            }

            if (self.isSortByAscending()) {
                return "ascending";
            }

            return "descending";
        };

        self.dispose = function () {
            self.noRowsSelected.dispose();
            self.allRowsSelected.dispose();
            self.partialRowsSelected.dispose();
            self = null;
        };
    }

    function renderHeaders(table) {
        var $head = $(table._head);

        if ($head.children().length > 0) {
            // The header row has been rendered and bindings have been applied.
            return Promise.resolve();
        }

        $head.append("<div class=\"dt-row\">");

        return include.loadTemplate(HEADER_ROW_TEMPLATE)
            .then(function () {
                ko.renderTemplate(HEADER_ROW_TEMPLATE, table._headerRowViewModel, {}, $head.children()[0], "replaceChildren");
            }).finally(function () {
                $head = null;
            });
    }

    function renderApplyFilterRow (table) {
        ko.applyBindings({
            applyFilters: function () {
                computeDataOptions(table, true);
            }
        }, table._applyFilterSection[0]);
    }

    function renderFilterRow (table) {
        var filterRowView = ko.dataFor(table._element);
        var $filterRowTemplate = $(table._filterRowTemplate());
        var $filterSection = $(table._filterSection);
        var $filterRow = $filterRowTemplate.clone();

        var wereFiltersModifiedPreRender = areFiltersModified(table);

        if (table._autoApplyFilters) {
            //determine which filter position has focus
            _.forEach($(FILTER_SELECTORS, table._filterSection), function(inputNode, index) {
                var $input = $(inputNode);
                if ($input.is(":focus")) {
                    table._focusFilterIndex = index;
                }
            });
        }

        //
        // Rerender the row
        //

        $filterSection.children().each(function (idx, ele) {
            ko.cleanNode(ele); // make sure addDisposeCallback's are called
        });

        $filterSection.empty();

        if ($(FILTER_SELECTORS, $filterRow).length === 0) {
            // Only display the filter row if the row template has something that can
            // be filtered.
            return;
        }

        $filterSection.append($filterRow);
        ko.applyBindings(filterRowView, $filterRow[0]);

        //
        // Attach input events to know when to display the "Apply Filter" row
        //
        _.forEach($(FILTER_SELECTORS, table._filterSection), function(inputNode, index) {
            var $input = $(inputNode);
            //focus correct filter
            if (table._autoApplyFilters && table._focusFilterIndex === index) {
                // workaround so that Firefox keeps the cursor at the end of the input
                var tmp = $input.val();
                $input.focus().val('').val(tmp);
            }

            // If we already have a filter modification pending, then
            // don't reset the saved data.  If we reset it, then when the pending modification processes
            // it won't see the change and will not filter.
            if (!wereFiltersModifiedPreRender) {
                $input.data(DATA_OLD_VALUE_KEY, $input.val());
            }
            $input.on(
                "propertychange change click keydown input paste",
                _.debounce(function () {
                    onFilterInputModification(table);
                }, table._filterDebounceDelay)
            ); // end "on"
        });

        //
        // Make sure we set the proper margin if table is scrolled when row is created
        //
        if ($('.scrollable-section').scrollLeft() > 0) {
            $filterRow.css({marginLeft: -($('.scrollable-section').scrollLeft()) + 'px'});
        }
    }

    function renderTopSummaryRow(table, bindingContext) {
        var rowData = ko.unwrap(table._options.summaryRow);
        var $rowTemplate = $(table._summaryRowTemplate());
        var $topSummary = $(table._topSummarySection);

        $topSummary.children().each(function(){
            ko.removeNode(this);
        });

        if (rowData) {
            var $row = $rowTemplate.clone();
            $topSummary.append($row);
            rowData = bindingHandlerUtils.decorateWithTemplateContext(rowData, bindingContext);
            ko.applyBindings(rowData, $row[0]);
        }
    }

    function renderBtmSummaryRow(table, bindingContext) {
        var rowData = ko.unwrap(table._options.summaryRow);
        var $rowTemplate = $(table._summaryRowTemplate());
        var $btmSummary = $(table._btmSummarySection);

        $btmSummary.children().each(function(){
            ko.removeNode(this);
        });

        if (rowData) {
            var $row = $rowTemplate.clone();
            $btmSummary.append($row);
            rowData = bindingHandlerUtils.decorateWithTemplateContext(rowData, bindingContext);
            ko.applyBindings(rowData, $row[0]);
        }
    }

    function renderContents(table, bindingContext) {
        var rows = ko.unwrap(table._options.rows);
        var $rowTemplate = $(table._rowTemplate());
        var $body = $(table._body);

        var $existingRows = $body.children();
        $existingRows.each(function() {
            ko.removeNode(this);
        });

        var selectedRowViewModels = ko.utils.peekObservable(table._options.selectedRows) || [];

        if (rows.length > 0) {
            _.each(rows, function (rowData) {
                var $row = $rowTemplate.clone();
                if (_.includes(selectedRowViewModels, rowData)) {
                    $row.addClass(ROW_ACTIVE_CLASS);
                }
                $body.append($row);
                rowData = bindingHandlerUtils.decorateWithTemplateContext(rowData, bindingContext);
                ko.applyBindings(rowData, $row[0]);
            });

            if (table._allowRowPinning) {
                computePinnedRows(table);
            }
        } else if (table._isLoadingRows()) {
            $body.append(getLoadingDataTemplate().clone());
        } else {
            $body.append(getNoDataTemplate(table._options));
        }

    }

    /**
     * Set the width of each column to account for the fact that the "l-scroll-table" layout
     * will not adjust the width of the columns/cells to automatically adjust its width
     * to have all content on a single line. To get around this, we utilize the
     * "l-auto-table" class to initially lay out the table and calculate the column widths.
     *
     * We then use these widths to explicitly set the width on each cell in a given column
     * in case the consumer wishes to use the "l-scroll-table" layout.
     */
    function fixColumns(table) {
        // In order to get the correct width of the columns, we need to have the data-table
        // render as a normal table layout by adding the "l-auto-table" layout class to
        // the root element. This will have each cell render as if it was in a <table>
        // element and have its width adjust for its content accordingly.
        //
        // Once the cells are laid out in a table, we want to hide the no data row because
        // if the first column is selectable, the columns widths won't render properly.
        // Once the row is hidden can then move on to setting the widths.
        $(table._element).addClass(TABLE_LAYOUT_CLASS)
                         .addClass(TABLE_AUTOSIZING)
                         .removeClass(SCROLL_LAYOUT_CLASS);

        var colWidths = [];

        // Reduce browser reflow by first calculating the widths we want for each column
        // and then setting them in a second loop.
        $(".dt-cell", table._head)
            .each(function (index, element) {
                colWidths.push($(element).outerWidth());
            });

        _.each(colWidths, function (width, index) {
            var childIndex = index + 1;
            $(".dt-cell:nth-child(" + childIndex + ")", table._element).css({
                "width": width
            });
        });

        // Show the no data row again since the columns are now sized properly
        $(table._element).removeClass(TABLE_AUTOSIZING);
    }

    function renderFooter(table) {
        if (_.isUndefined(table._footerTemplate)) {
            return;
        }

        var $element = $(table._element);
        var $footer = $(table._footerTemplate).clone();

        $element.after($footer);

        var showCustomizeColumns = _.has(table._options, "showCustomizeColumns") ? table._options.showCustomizeColumns : true;
        var customButtons = _.map(table._options.customButtons || [], function (btn) {
            return {
                faIconClass: btn.faIconClass,
                buttonKey: btn.buttonKey,
                onClick: function () {
                    btn.onClick();
                    table._isDisplayingCustomizeOptions(false);
                }
            };
        });

        ko.applyBindings({
            displayColumnConfiguration: function (event) {
                table._isDisplayingCustomizeOptions(false);
                onDisplayColumnConfiguration(event, table);
            },
            resetDataTable: function (event) {
                table._isDisplayingCustomizeOptions(false);
                table._isReset(true);
                onTableReset(event, table);
            },
            showReset : function () {
                if (_.has(table._options, "onTableReset")) {
                    return true;
                }
                return false;
            },
            isDisplayingCustomizeOptions: table._isDisplayingCustomizeOptions,
            isLoadingRows: table._isLoadingRows,
            pager: table._pagerView,
            alternatePager: table._alternatePager,
            customButtons: customButtons,
            showCustomizeColumns: showCustomizeColumns
        }, $footer[0]);
    }

    function adjustForScrollbar(table) {
        if (isDisplayingVerticalScrollbar(table._body)) {
            var margin = domUtils.getScrollbarWidth();

            if (table._head) {
                table._head.find(".dt-row").css({
                    "margin-right": margin
                });
            }

            if (table._hasFilterRow && table._filterSection) {
                table._filterSection.find(".dt-row").css({
                    "margin-right": margin
                });
            }

            if (table._hasSummaryRow()) {
                if (table._topSummarySection) {
                    table._topSummarySection.find(".dt-row").css({
                        "margin-right": margin
                    });
                }

                if (table._btmSummarySection) {
                    table._btmSummarySection.find(".dt-row").css({
                        "margin-right": margin
                    });
                }
            }
        }

        if (table._body) {
            var minHeight = $(".dt-row", table._body).height();
            if (isDisplayingHorizontalScrollbar(table._body)) {
                minHeight += domUtils.getScrollbarWidth();
            }
            var minHeightCap = table._options.minBodyHeightCap || 200;

            minHeight = Math.min(minHeightCap, minHeight);

            table._body.css({
                "min-height": minHeight
            });
        }
    }

    function adjustHeadColumnAlignment(table) {

        var row = $(table._body).find(".dt-row")[0];

        $(row).find(".dt-cell").each(function(){

            var $cell = $(this);
            var cellType = [
                "number-cell",
                "action-cell",
                "selectable-cell",
                "pinnable-cell"
            ];

            _(cellType).forEach(function(cellTypeStr) {
                if($cell.hasClass(cellTypeStr)){
                    var cellId = $cell.data('column');
                    table._head.find(".dt-column-" + cellId).addClass(cellTypeStr);
                }
            });

        });

    }

    function renderTable(table, bindingContext) {
        renderHeaders(table)
            .then(function () {
                fixColumns(table);

                if (table._layoutType === SCROLL_LAYOUT_CLASS) {
                    $(table._element)
                        .addClass(SCROLL_LAYOUT_CLASS)
                        .removeClass(TABLE_LAYOUT_CLASS);

                    adjustForScrollbar(table);
                }

                adjustHeadColumnAlignment(table);

                updateSelectedRows(table);

                if (table._options.onRenderComplete) {
                    table._options.onRenderComplete();
                }
            });

        if (table._hasFilterRow) {
            renderFilterRow(table);
        }

        if (table._hasSummaryRow()) {
            renderTopSummaryRow(table, bindingContext);
        }

        renderContents(table, bindingContext);

        if (table._hasSummaryRow()) {
            renderBtmSummaryRow(table, bindingContext);
        }

    }

    function calculateTableState (table) {
        var data = ko.unwrap(table._options.dataOptions);

        if (data) {
            if (_.has(table, "_pagerView")) {
                // Set the pageCount before the pageNumber.  The pageNumber write will check that the pageNumber is
                // < the pageCount, so the first time this runs pageNumber: 2 would reset back to 1.
                table._pagerView.pageCount(data.pageCount);
                table._pagerView.pageNumber(data.pageNumber);
                table._pagerView.pageSize(data.pageSize);
            }

            table._sortByColumnName(data.sortByColumnName);
            table._isSortByAscending(data.isSortByAscending);
        }
    }


    function computeHiddenColumnDataCollection (table) {
        return _.reject(table._columnMultiselectData, function filterVisibleColumns (column) {
            return _.indexOf(ko.unwrap(table._options.visibleColumnIds), column.id) !== -1;
        });
    }

    function computeVisibleColumnDataCollection (table) {
        return _.filter(table._columnMultiselectData, function filterVisibleColumns (column) {
            return _.indexOf(ko.unwrap(table._options.visibleColumnIds), column.id) !== -1;
        });
    }

    function computeColumnData (table) {
        var visibleColumnData = computeVisibleColumnDataCollection(table);
        var hiddenColumnData  = computeHiddenColumnDataCollection(table);

        // Order visible columns so that the order matches the
        // order set to in visibleColumnIds observable.
        var sortedVisibleColumns = _.sortBy(visibleColumnData, [function(column) {
            var visibleColumns = ko.unwrap(table._options.visibleColumnIds);
            return visibleColumns.indexOf(column.id);
        }]);

        table._hiddenColumnData(_.sortBy(hiddenColumnData, 'name'));
        table._visibleColumnData(sortedVisibleColumns);
        table._visibleColumnSum(sortedVisibleColumns.length);
    }

    var computeDataOptions = _.debounce(function (table, resetPageNumber) {
        var dataOptions = {};

        if (_.has(table, "_pagerView")) {
            // reset to page one if filter changed
            if(resetPageNumber){
                table._pagerView.pageNumber(1);
            }

            // reset to page one if current page size
            // is different from new pager size.
            if(table._options.dataOptions().pageSize !== table._pagerView.pageSize()){
                table._pagerView.pageNumber(1);
            }

            dataOptions.pageNumber = table._pagerView.pageNumber();
            dataOptions.pageSize   = table._pagerView.pageSize();
        }

        dataOptions.sortByColumnName  = table._sortByColumnName();
        dataOptions.isSortByAscending = table._isSortByAscending();

        if (_.has(table._options, "onDataOptionsChange")) {
            table._isLoadingRows(true);
            table._options.onDataOptionsChange(dataOptions);
        }

        if (_.has(table, "_pagerView")) {
            if (resetPageNumber) {
                // Update page count if the filter has decreased the number of entries
                if (table._options.dataOptions().pageCount !== table._pagerView.pageCount()) {
                    table._pagerView.pageCount(table._options.dataOptions().pageCount);
                }
            }
        }
        if (table._applyFilterSection) {
            table._applyFilterSection.addClass(HIDDEN_CLASS);
        }
        saveFilterValues(table);
    }, 10);

    function computePinnedRows (table) {
        if (!table._options.allowRowPinning) {
            return;
        }

        var pinnedRowViewModels = ko.unwrap(table._options.pinnedRows);
        var $rowNodes = $(".dt-row", table._body);
        var pinnedRowNodes = [];

        _.each($rowNodes, function (rowNode) {
            var viewModelData = ko.dataFor(rowNode);

            var isPinnedRow = _.find(pinnedRowViewModels, function (pinnedRowView) {
                return pinnedRowView === viewModelData;
            });

            if (!_.isUndefined(isPinnedRow)) {
                pinnedRowNodes.push(rowNode);
            }
        });

        $rowNodes.removeClass(ROW_PINNED_CLASS);
        $rowNodes.removeClass(LAST_ROW_PINNED_CLASS);
        $(pinnedRowNodes).addClass(ROW_PINNED_CLASS);
        $(_.last(pinnedRowNodes)).addClass(LAST_ROW_PINNED_CLASS);
        computeDataOptions(table, false);
    }

    function computeActiveRows (table) {
        var selectedRowViewModels = ko.unwrap(table._options.selectedRows);

        var $rowNodes = $(".dt-row", table._body);
        var selectedRowNodes = [];

        _.each($rowNodes, function (rowNode) {
            var viewModelData = ko.dataFor(rowNode);

            var isSelectedRow = _.find(selectedRowViewModels, function (selectedRowView) {
                return selectedRowView === viewModelData;
            });

            if (!_.isUndefined(isSelectedRow)) {
                selectedRowNodes.push(rowNode);
            }
        });

        $rowNodes.removeClass(ROW_ACTIVE_CLASS);
        $(selectedRowNodes).addClass(ROW_ACTIVE_CLASS);
    }



    //
    // Render Settings Dialog
    //

    // transferBox observables
    var modifiedColumns = ko.observableArray();
    var visibleColumnData = ko.observableArray();
    var visibleColumnSum = ko.observable();
    var availableColumns = ko.observableArray();

    function displayColumnConfiguration (table) {
        var $colConfigSection = $(table._columnConfigurationSection);

        // stored state for cancellation
        var initialVisibleColumns = table._visibleColumnData.peek().slice(0);
        var initialAvailableColumns = table._columnMultiselectData.slice(0);

        // update the observables for the dialog
        modifiedColumns([]);
        visibleColumnData(initialVisibleColumns.slice(0));
        visibleColumnSum(visibleColumnData.peek().length);
        availableColumns(table._columnMultiselectData.slice(0));

        table._isDisplayingColumnConfiguration(true);

        if (table._isReset()) {
            // If we reset the table, we need to always re-apply bindings to get the changes
            ko.cleanNode($colConfigSection[0]);
            table._isReset(false);
        } else if ($colConfigSection.children().length > 0) {
            return;
        }

        ko.applyBindings({
            availableColumns: availableColumns,
            isDisplayingColumnConfiguration: table._isDisplayingColumnConfiguration,
            modifiedColumns:   modifiedColumns,
            visibleColumnData: visibleColumnData,
            visibleColumnSum:  visibleColumnSum,
            shouldSort: (visibleColumnSum() ? true : false),
            filterHiddenColumns: function (query) {
                var filteredColumns;

                // Get the list of columns that are currently set as "hidden"
                var visibleColumnIds = _.map(visibleColumnData(), "id");
                var hiddenColumnData = _.reject(table._columnMultiselectData, function filterVisibleColumns (column) {
                    return _.indexOf(visibleColumnIds, column.id) !== -1;
                });

                // Filter the columns according to the provide query string
                if (_.isEmpty(query)) {
                    filteredColumns = hiddenColumnData;
                } else {
                    filteredColumns = _.filter(hiddenColumnData, function (column) {
                        return column.name.toLowerCase().indexOf(query.toLowerCase()) !== -1;
                    });
                }

                availableColumns(filteredColumns);
            },
            cancelColumnConfiguration: function () {
                table._isDisplayingColumnConfiguration(false);
                ko.cleanNode($colConfigSection[0]);

                //reset state of binding
                availableColumns(initialAvailableColumns.slice(0));
                visibleColumnData(initialVisibleColumns.slice(0));
                visibleColumnSum(visibleColumnData().length);
                modifiedColumns.removeAll();
            },
            saveColumnSettings: function () {
                var visibleColumnIds = _.map(visibleColumnData(), 'id');

                var columnsToHide = _.filter(modifiedColumns(), function (column) {
                    return _.indexOf(visibleColumnIds, column.id) === -1;
                });

                if (table._hasFilterRow) {
                    // If a column gets hidden and has a filter input on it, then we have
                    // to clear those filters and update the data options of the table.
                    var didAnyInputValueChange = false;

                    _.each(columnsToHide, function (column) {
                        var $inputNodes = $('div[data-column="' + column.id + '"] ' + FILTER_SELECTORS, table._filterSection);
                        var didValueChange = false;
                        if ($inputNodes.length > 0) {
                            _.each($inputNodes, function (input) {
                                var $input = $(input);

                                // Make sure we empty out all input filters in a cell.
                                if (!_.isEmpty($input.val())) {
                                    $input.val("");
                                    didValueChange = true;
                                    didAnyInputValueChange = true;
                                }
                            });
                            // trigger a change event so that the bindings that track input
                            // changes are executed and update the view model's observable.
                            if (didValueChange) {
                                $inputNodes.change();
                            }
                        }
                    });

                    if (didAnyInputValueChange) {
                        computeDataOptions(table, false);
                    }
                }

                table._options.visibleColumnIds(visibleColumnIds);
                table._isDisplayingColumnConfiguration(false);
            }
        }, $colConfigSection[0]);
    }



    // ===================================================================================
    // Events
    // ===================================================================================

    function onDisplayColumnConfiguration (event, table) {
        displayColumnConfiguration(table);
    }

    function onTableReset (event, table) {
        if (_.has(table._options, "onTableReset")) {
            table._options.onTableReset();
            computeDataOptions(table, false);
        }
    }

    function onFilterSubmit (event, table) {
        if (event.keyCode === keyCode.ENTER) {
            event.preventDefault();
            computeDataOptions(table, true);
        }

        if (getLayoutClass($(table)) === SCROLL_LAYOUT_CLASS) {
            var $scrollable = $('.scrollable-section');
            var $target = $(event.target).parents('.filter-cell');

            if ($scrollable.length > 0) {
                if (($scrollable[0].offsetWidth + $scrollable.scrollLeft()) - ($target[0].scrollWidth + $target[0].offsetLeft) < 0) {
                    var offset = $target[0].scrollWidth - (($scrollable[0].offsetWidth + $scrollable[0].offsetLeft) - $target[0].offsetLeft);
                    $scrollable.scrollLeft(offset);
                }
            }
        }
    }

    var onSectionScroll = _.debounce(function (event) {
        var $scroller = $(event.target);

        $scroller
            .siblings('.dt-section:not(.fixed-section)')
                .find('.dt-row').each(function () {
                    $(this).css({marginLeft: -$scroller.scrollLeft() + 'px'});
                });
    }, 10);

    function onRowClick(event, table) {
        if (ko.unwrap(table._options.rows).length === 0) {
            return;
        }

        var $targetCell = $(event.target).hasClass('dt-cell') ? $(event.target) : $(event.target).parents(".dt-cell");
        var $targetRowNode = $targetCell.parents(".dt-row");

        if ($targetCell.hasClass(PINNABLE_CELL)) {
            $targetRowNode.toggleClass(ROW_PINNED_CLASS);

            updatePinnedRows(table);
        } else {
            var selectedRowIndex = $targetRowNode.index();
            var lastSelectedRowIndex = ko.utils.domData.get(table._element, TABLE_DOMDATA_KEY);

            if (table._isMultiRowSelection) {
                if (event.shiftKey && !_.isUndefined(lastSelectedRowIndex) && selectedRowIndex != lastSelectedRowIndex) {
                    var startIndex = Math.min(selectedRowIndex, lastSelectedRowIndex);
                    var endIndex   = Math.max(selectedRowIndex, lastSelectedRowIndex);

                    var $selectedRows = $(".dt-row", table._body)
                                            .slice(startIndex, endIndex + 1);

                    if ($targetRowNode.hasClass(ROW_ACTIVE_CLASS)) {
                        $selectedRows.removeClass(ROW_ACTIVE_CLASS);
                    } else {
                        $selectedRows.addClass(ROW_ACTIVE_CLASS);
                    }

                    // Prevent text highlighting when the user is selecting multiple rows.
                    window.getSelection().removeAllRanges();
                } else {
                    $targetRowNode.toggleClass(ROW_ACTIVE_CLASS);
                }
            } else if (table._isSingleRowSelection) {
                var shouldSetActive = !$targetRowNode.hasClass(ROW_ACTIVE_CLASS);

                $("." + ROW_ACTIVE_CLASS, table._body).removeClass(ROW_ACTIVE_CLASS);

                if (shouldSetActive) {
                    $targetRowNode.addClass(ROW_ACTIVE_CLASS);
                }
            }

            ko.utils.domData.set(table._element, TABLE_DOMDATA_KEY, selectedRowIndex);

            updateSelectedRows(table);
        }
    }

    var onWindowResize = function (table) {
        return _.debounce(function () {
            adjustForScrollbar(table);
        }, 100);
    };

    // ===================================================================================
    // Class Definition
    // ===================================================================================

    function DataTable(element, options, bindingContext) {
        var $element = $(element);
        var table = this;

        this._element = element;
        this._options = options;
        this._uniqueEventNamespace = _.uniqueId(EVENT_NAMESPACE);
        this._autoApplyFilters = _.has(options, 'autoApplyFilters') ? ko.unwrap(options.autoApplyFilters) : false;
        this._filterDebounceDelay = this._autoApplyFilters ? AUTO_FILTER_DEBOUNCE_DELAY : MANUAL_FILTER_DEBOUNCE_DELAY;
        this._layoutType            = getLayoutClass($element);
        this._allowRowPinning       = $(this._element).find("[data-pinnable]").length > 0;
        this._isMultiRowSelection   = $(this._element).find("[data-selectable]").length > 0;
        this._isSingleRowSelection  = $(this._element).find("[data-selectable-single]").length > 0;
        this._hasFilterRow          = $(this._element).find("[data-filter-row]").length > 0;
        this._focusFilterIndex = -1;
        this._hasSummaryRow         = ko.observable(false);
        this._sortByColumnName      = ko.observable();
        this._isSortByAscending     = ko.observable();
        this._columnMultiselectData = _.map(ko.unwrap(this._options.columns), function (column) {
            if(column.text){
                return {
                    id: column.id,
                    name: column.label
                };
            } else {
                return {
                    id: column.id,
                    name: ko.i18n.renderString(column.label)
                };
            }
        });

        // check for summary row
        if(this._options.summaryRow){
            this._hasSummaryRow(true);
        }

        //If you pass in a loading observable, you can control the loading indicator
        this._isLoadingRows = ko.observable();
        if (_.has(table._options, "loading") && ko.isObservable(table._options.loading)){
            this._isLoadingRows = table._options.loading;
        } else if (_.has(table._options, "rows") && table._options.rows().length > 0){
            this._isLoadingRows(false);
        } else {
            this._isLoadingRows(true);
        }


        //If you pass in an isDisplayingColumnConfiguration Observable, you can control the column configuration dialog visibility
        this._isDisplayingColumnConfiguration = ko.observable();
        if (_.has(table._options, "isDisplayingColumnConfiguration") && ko.isObservable(table._options.isDisplayingColumnConfiguration)){
            this._isDisplayingColumnConfiguration = table._options.isDisplayingColumnConfiguration;
        } else {
            this._isDisplayingColumnConfiguration(false);
        }

        //
        // Column Configuration
        //
        this._hiddenColumnData     = ko.observableArray();
        this._visibleColumnData    = ko.observableArray();
        this._visibleColumnSum     = ko.observable();

        this._visibleColumns       = ko.computed(function () {
            if (_.has(table._options, "visibleColumnIds")) {
                var columns =  _.filter(ko.unwrap(table._options.columns), function (column) {
                    return _.indexOf(ko.unwrap(table._options.visibleColumnIds), column.id) !== -1;
                });

                // Order visible columns so that the order matches the
                // order set to in visibleColumnIds observable.
                var sortedColumns = _.sortBy(columns, [function(column) {
                    var visibleColumns = ko.unwrap(table._options.visibleColumnIds);
                    return visibleColumns.indexOf(column.id);
                }]);

                return sortedColumns;
            }

            // If the consumer has not defined the ```visibleColumnIds```, then all columns
            // are visible by default.
            return table._options.columns;
        });

        this._updateColumnDataComputed = ko.computed(computeColumnData.bind(null, this));

        //
        // Template Calculation
        //
        this._filterColumnTemplates  = buildFilterColumnTemplates(this);
        this._contentColumnTemplates = buildContentColumnTemplates(this);
        this._summaryColumnTemplates = buildSummaryColumnTemplates(this);
        this._filterRowTemplate      = ko.computed(buildFilterRowTemplate.bind(null, this));
        this._rowTemplate            = ko.computed(buildContentRowTemplate.bind(null, this));
        this._summaryRowTemplate     = ko.computed(buildSummaryRowTemplate.bind(null, this));
        this._footerTemplate         = buildFooterTemplate(this);



        //
        // Computeds for maintaining data state
        //

        // Keep the state of the pager and data options in sync
        this._tableStateComputed = ko.computed(calculateTableState.bind(null, this));

        var subscriptions = [
            this._sortByColumnName,
            this._isSortByAscending
        ];


        if (_.has(this, "_pagerView")) {
            subscriptions.push(this._pagerView.pageNumber);
            subscriptions.push(this._pagerView.pageSize);
        }

        // Use a subscription here so that the registered "onDataOptionsChange" callback
        // is not executed when the binding is evaluated for the first time. The initial
        // loading of data should be determined by the consumer
        this._computedDataOptionsSubscription = ko.multiSubscribe(
            subscriptions,
            computeDataOptions.bind(null, this, false)
        );

        this._rowChangeSubscription = this._options.rows.subscribe(function () {
            table._isLoadingRows(false);
        });

        //
        // Table Rendering
        //

        $element.empty();

        this._head                       = $('<div class="dt-section dt-section-head hidden"></div>');
        this._body                       = $('<div class="dt-section dt-section-body scrollable-section"></div>');
        this._topSummarySection          = $('<div class="dt-section dt-section-summary dt-section-summary-top"></div>');
        this._btmSummarySection          = $('<div class="dt-section dt-section-summary dt-section-summary-btm"></div>');
        this._columnConfigurationSection = $('<div class="dt-column-settings" data-bind="dialog: isDisplayingColumnConfiguration, include: \'templates/widgets/dataTable/columnVisibilityConfiguration\'"></div>');
        this._filterSection              = null;
        this._applyFilterSection         = null;

        if (this._hasFilterRow) {
            this._filterSection = $('<div class="dt-section dt-section-filter fixed-vertical-section hidden"></div>');
            this._applyFilterSection = $('<div class="dt-section dt-section-apply-filter fixed-section hidden" data-bind="include: \'templates/widgets/dataTable/applyFilterRow\'"></div>');

            if(this._hasSummaryRow()){
                $element.append(this._head, this._filterSection, this._applyFilterSection, this._topSummarySection, this._body, this._btmSummarySection, this._columnConfigurationSection);
            } else {
                $element.append(this._head, this._filterSection, this._applyFilterSection, this._body, this._columnConfigurationSection);
            }

            renderApplyFilterRow(this);
        } else {
            if(this._hasSummaryRow()){
                $element.append(this._head, this._topSummarySection, this._body, this._btmSummarySection, this._columnConfigurationSection);
            } else {
                $element.append(this._head, this._body, this._columnConfigurationSection);
            }
        }

        renderFooter(this);

        this._body.attr("tabindex", "0");

        this._headerRowViewModel = new HeaderRowViewModel(this);
        this._renderComputed = ko.computed(renderTable.bind(null, this, bindingContext));
        this._activeRowsComputed = ko.computed(computeActiveRows.bind(null, this));
        this._pinnedRowsComputed = ko.computed(computePinnedRows.bind(null, this));


        //
        // Event Registration
        //
        if(!table._autoApplyFilters && this._hasFilterRow) {
            this._filterSection.on("keydown" + EVENT_NAMESPACE, function (event) {
                onFilterSubmit(event, table);
            });
        }

        if (this._layoutType === SCROLL_LAYOUT_CLASS) {
            $(".scrollable-section", $element).on("scroll" + EVENT_NAMESPACE, onSectionScroll);
        }

        var listenToClicks = false;
        if (this._isSingleRowSelection || this._isMultiRowSelection) {
            if (!_.has(this._options, "selectedRows") || !_.isArray(ko.unwrap(this._options.selectedRows))) {
                throw new Error("DataTable - selectable rows requires an Observable or Native Array to be passed into the \"selectableRows\" argument of the binding handler.");
            }

            listenToClicks = true;
        }

        if (this._allowRowPinning) {
            if (!_.has(this._options, "pinnedRows") || !_.isArray(ko.unwrap(this._options.pinnedRows))) {
                throw new Error("DataTable - pinned rows requires an Observable or Native Array to be passed into the \"pinnedRows\" argument of the binding handler.");
            }

            listenToClicks = true;
        }

        if (listenToClicks) {
            this._body.on('click' + EVENT_NAMESPACE, function (event) {
                onRowClick(event, table);
            });
        }

        $(window).on('resize' + this._uniqueEventNamespace, onWindowResize(this));
    }

    DataTable.prototype = {
        dispose: function () {
            this._filterRowTemplate.dispose();
            this._summaryRowTemplate.dispose();
            this._rowTemplate.dispose();
            this._renderComputed.dispose();
            this._tableStateComputed.dispose();
            this._computedDataOptionsSubscription.dispose();
            this._activeRowsComputed.dispose();
            this._pinnedRowsComputed.dispose();
            this._visibleColumns.dispose();
            this._updateColumnDataComputed.dispose();
            this._rowChangeSubscription.dispose();
            this._headerRowViewModel.dispose();

            $(".scrollable-section", this._element).off(EVENT_NAMESPACE);
            $(this._body).off(EVENT_NAMESPACE);
            $(this._element).off(EVENT_NAMESPACE);
            $(window).off(this._uniqueEventNamespace);

            if (this._hasFilterRow) {
                this._filterSection.off(EVENT_NAMESPACE);
                $('datatable-apply-filter-action', this._applyFilterSection).off('click');
            }

            if (!_.isUndefined(this._scrollSubscribe)) {
                this._scrollSubscribe.dispose();
            }

            this._element = null;
            this._columnConfigurationSection = null;
            this._filterColumnTemplates = null;
            this._contentColumnTemplates = null;
            this._summaryColumnTemplates = null;
            this._footerTemplate = null;
            this._btmSummarySection = null;
            this._topSummarySection = null;
            this._body = null;
            this._applyFilterSection = null;
            this._filterSection = null;
            this._head = null;
            this._rowTemplate = null;
        }
    };

    // ===================================================================================
    // Binding Definition
    // ===================================================================================

    /**
     * Render a data table. This binding requires special markup to be defined on the
     * element's children in order to work properly. Specifically, it requires the consumer
     * to define, in markup, what the markup for the rows in the table should look like.
     *
     * @param {Object} contentObj - An object that defines the content of the data table.
     *
     * @param {Object[]} contentObj.columns - An array containing objects that define the
     *  content of the column headers. Each object should contain the following parameters:
     *
     *     id: "String" - the id is used to map this object with the column markup
     *      defined by the "data-column" attribute in the template.
     *     label: "String" - The message key of the externalized string that should be used
     *      to render the content of the column's header.
     *
     * @param {Object[]|ViewModel[]} contentObj.rows - An observable array that contains
     *  the view objects that will be used to populate the contents of each rendered row.
     *
     * @param {Object[]|ViewModel[]} contentObj.dataOptions - An observable containing an
     *  object that defines what the table should display for its data options' values. Valid
     *  parameters of this object are:
     *
     *      * {Integer} dataOptions.pageCount - The amount of pages available for the displayed collection
     *      * {Integer} dataOptions.pageNumber - The current page number of the displayed collection
     *      * {Integer} dataOptions.pageSize - The max amount of items to display for the current page of the collection
     *
     *  Note: This argument will not have new values written to it by the dataTable binding - it is only read
     *  by th binding. To react to state changes to these values in the view model, one must use the ```onDataOptionsChange```
     *  callback.
     *
     * @param {Function} contentObj.onDataOptionsChange - A callback function that will execute whenever the data table
     *  modifies its internal state of the data options (paging, sorting, filtering, etc.). This callback is designed to
     *  allow view models to respond to this state change and update the displayed collection accordingly.
     *
     * @param {String[]} [contentObj.visibleColumnIds=undefined] - An observable array that will
     *  contain the IDs of the columns that should be visible in the table.
     *
     * @param {Object[]|ViewModel[]} [contentObj.selectedRows=undefined] - An observable array that
     *  contains the view objects for the rows that have been selected by the end user.
     *  This collection should be an array regardless of whether multi or single row
     *  selection is enabled.
     *
     *
     *
     * # Example 1 - Basic Table
     *
     * <div data-bind="dataTable: argObject">
     *     <div data-row data-bind="attr: { 'data-id': id }>
     *         <div data-column="name" class="assigned-date">
     *             <a data-bind="
     *              text: name,
     *              attr: {href: '#admin/interactions/' + conversation.id + '/evaluations/' + id}
     *             "></a>
     *         </div>
     *
     *         <div data-column="status" class="status">
     *             <span data-bind="lString: 'evaluatorDashboard.status.' + status"></span>
     *         </div>
     *     </div>
     * </div>
     *
     * In this example, we have a root element that declares that the "dataTable" binding
     * should be used. It requires a complex object ("argObject") that will be used to
     * figure out the content of the header row's columns as well as the data to render in
     * the content of each row.
     *
     * For the second div that declares the "data-row" attribute, this is the element in
     * which the template for a row's content is defined. Any data-bindings attached to
     * this element will be executed in the final table on each rendered row.
     *
     * Moving to the two column divs, each one declares a "data-column" attribute. The value
     * of this attribute is used as an ID to map its contents to the column id defined
     * in argObject.columns. In this case, the data-column="name" will result in the that
     * cell's contents being aligned with the appropiate header. The contents of that div
     * will then be used as the template for that column's cell in each row.
     *
     * Another point worth noting is that the binding context of each cell will be the view model
     * that defines the content of the row. So in the above example, for the anchor:
     *
     *     <div data-column="name" class="assigned-date">
     *         <a data-bind="
     *             text: name,
     *             attr: {href: '#users/' + id}
     *         "></a>
     *     </div>
     *
     * The "name" and "id" references in the data-bind attribute must be the names of
     * methods on the view model defined for the row.
     *
     *
     *
     * # Example 2 - Footer / Pager / Column Visibility
     *
     * <div data-bind="dataTable: argObject">
     *     <div data-row data-bind="attr: { 'data-id': id }>
     *         <div data-column="name" class="assigned-date">
     *             <a data-bind="
     *              text: name,
     *              attr: {href: '#admin/interactions/' + conversation.id + '/evaluations/' + id}
     *             "></a>
     *         </div>
     *
     *         <div data-column="status" class="status">
     *             <span data-bind="lString: 'evaluatorDashboard.status.' + status"></span>
     *         </div>
     *     </div>
     *
     *     <div data-footer>
     *         <div data-pager></div>
     *         <div data-settings></div>
     *     </div>
     * </div>
     *
     * In this example, I would like to call out the "data-footer" div at the bottom of
     * the root's content. This is an optional element that allows one to declare the
     * contents of the data table's footer. If the data-footer is defined, it will need
     * at least one valid child element to also be rendered. The following (optional)
     * elements are available for the footer:
     *
     *      * data-pager: This will render a Pager widget in the footer. The state of the
     *         pager will be stored in the observable passed to the "dataOptions" argument
     *         for the binding.
     *      * data-settings: This will render a dropdown button that will allow the user to
     *         modify how the columns are organized in the table as well as a shortcut for
     *         resetting the table's state to its default.
     *
     *         **NOTE**: To take advantage of the column configuration settings UI, the
     *         consumer will need to include an observable array as part of the
     *         ```visibleColumnIds``` argument.
     *
     *
     *
     *
     *
     * # Example 3 - Sorting
     *
     * <div data-bind="dataTable: argObject">
     *     <div data-row data-bind="attr: { 'data-id': id }>
     *         <div data-column="name" data-sortable class="assigned-date">
     *             <a data-bind="
     *              text: name,
     *              attr: {href: '#admin/interactions/' + conversation.id + '/evaluations/' + id}
     *             "></a>
     *         </div>
     *
     *         <div data-column="status" class="status">
     *             <span data-bind="lString: 'evaluatorDashboard.status.' + status"></span>
     *         </div>
     *     </div>
     * </div>
     *
     * In this example, we declare a column to be sortable when the user clicks on the
     * header of the column. To enable this functionality, we add the "data-sortable" data
     * attribute to the data-column(s) in which we want to enable sorting. In the code
     * above, this is enabled on the first data-column entry.
     *
     * When this is enabled, the callback passed into the onDataOptionsChange argument
     * will receive the following arguments on the argument object passed into the callback:
     *
     *   - sortByColumnName (String) - This will be the value passed into the data-column
     *      argument of the sorted column.
     *   - isSortByAscending (Bool) - A boolean that determines the requested sort order.
     *      If true, then the sort order of the column should be ascending. If false, then
     *      the order should be descending.
     *
     *
     *
     * # Example 4 - Row Selection
     *
     *  <div data-bind="dataTable: argObject">
     *     <div data-row data-selectable>
     *         <div data-column="name" data-sortable class="assigned-date">
     *             <a data-bind="
     *              text: name,
     *              attr: {href: '#admin/interactions/' + conversation.id + '/evaluations/' + id}
     *             "></a>
     *         </div>
     *
     *         <div data-column="status" class="status">
     *             <span data-bind="lString: 'evaluatorDashboard.status.' + status"></span>
     *         </div>
     *     </div>
     * </div>
     *
     * In this example, to enable row selection, one needs to add the ```data-selectable```
     * attribute to their data-row element. When this attribute is found, the data table
     * will display the required elements to enable multiple row selection. Whenever the
     * user selects a row, the view model used to render the selected row's contents will
     * be added to the ```selectedRows``` attribute's value collection that was passed
     * in with the ``argObject```.
     *
     * To enable SINGLE row selection, you just need to replace ```data-selectable``` with
     * ```data-selectable-single``` instead:
     *
     * <div data-bind="dataTable: argObject">
     *     <div data-row data-selectable-single>
     *         <div data-column="name" data-sortable class="assigned-date">
     *             <a data-bind="
     *              text: name,
     *              attr: {href: '#admin/interactions/' + conversation.id + '/evaluations/' + id}
     *             "></a>
     *         </div>
     *
     *         <div data-column="status" class="status">
     *             <span data-bind="lString: 'evaluatorDashboard.status.' + status"></span>
     *         </div>
     *     </div>
     * </div>
     *
     *
     *
     * # Example 5 - Search / Filtering
     *
     *  <div data-bind="dataTable: argObject">
     *      <div data-filter-row>
     *          <div data-column="name">
     *              <input class="form-control input-sm" type="text" data-bind="textInput: nameQuery"/>
     *           </div>
     *       </div>
     *
     *     <div data-row data-selectable>
     *         <div data-column="name" data-sortable class="assigned-date">
     *             <a data-bind="
     *              text: name,
     *              attr: {href: '#admin/interactions/' + conversation.id + '/evaluations/' + id}
     *             "></a>
     *         </div>
     *
     *         <div data-column="status" class="status">
     *             <span data-bind="lString: 'evaluatorDashboard.status.' + status"></span>
     *         </div>
     *     </div>
     * </div>
     *
     * In this example, we enable column-based search. To enable this feature, we create an
     * element with the ```data-filter-row``` attribute. This element works just like the
     * ```data-row```, specifically, its children nodes should have a ```data-column``` attribute
     * that corresponds to the key name of the column that the template should be applied
     * to.
     *
     * The differences in ```data-filter-row``` is that the view model used to render the
     * filter row will be the main view model that backs the entire dataTable binding (whereas
     * the ```data-row``` use the indexed objects provided in the ```rows``` collection as
     * the view model).
     *
     * When the filter are applied, the dataTable will execute the ```onDataOptions```
     * callback provided, but the values stored in the filters will not be passed into
     * the callback. If you wish to utilize the filters that the user provided, you will
     * need to reference the attributes that store those values. For example:
     *
     *
     * var MyView = function () {
     *     var self = this;
     *
     *     self.nameQuery = ko.observable();
     *
     *     self.dataOptionsCallback = function (dataOptions) {
     *         var args = {
     *             pageNumber: dataOptions.pageNumber,
     *             pageSize:   dataOptions.pageSize,
     *             name:       self.nameQuery()
     *         };
     *
     *         return userService.getUsers(args);
     *     };
     * };
     *
     *
     * **NOTE** It is also recommended that the binding you use to track your filter input's
     * value is one that will update as the user types (e.g. the ```textInput``` binding or
     * ```valueUpdate: keyUp```). This will ensure that the ```onDataOptionsChange``` will
     * execute with the most recent query values provided by the user.
     *
     */
    ko.bindingHandlers.dataTable = {
        init: function (element, valueAccessor, allBindings, viewModel, bindingContext) {
            $(element).addClass(DATA_TABLE_CLASS);
            var options = ko.unwrap(valueAccessor());
            options.columns = ko.unwrap(options.columns);
            options.noDataTextKey = ko.unwrap(options.noDataTextKey) || 'dataGrid.empty';

            var grid = new DataTable(element, options, bindingContext);

            if(ko.isObservable(options.rows)){
                options.rows.extend({ notify: 'always'});
            }

            var isLoadingRowsComputed = ko.computed(function() {
                if (grid._isLoadingRows() === false && !_.isUndefined(ko.unwrap(options.rows))) {
                    //Show head and filter sections once the data has finished loading
                    if(_.hasValue(grid._head)) {
                        grid._head.removeClass(HIDDEN_CLASS);
                    }

                    if (_.hasValue(grid._filterSection)) {
                        grid._filterSection.removeClass(HIDDEN_CLASS);
                    }
                }
            });
            ko.utils.domNodeDisposal.addDisposeCallback(element, function () {
                grid.dispose();
                isLoadingRowsComputed.dispose();
            });

            return {controlsDescendantBindings: true};
        }
    };
});

